import { useCallback, useRef } from 'react';
import { notifyMessageRecipient } from '../actions/matrixAction';
import { ROLE } from '../constants/common';
import { captureChatException } from '../sentry';
import { shouldSkipPrioritySubscriberNotify } from '../utils/chatUtil';
import { isLoginWithSupportPassword } from '../utils/function';

const MESSAGE_NOTIFY_PREVIEW_MAX_LENGTH = 200;

/**
 * @param {number|string|null|undefined} role
 * @returns {number|null}
 */
const normalizeUserRole = (role) => {
  if (role == null || role === '') return null;
  const parsed = Number(role);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * @param {string} text
 * @returns {string}
 */
const truncatePreviewText = (text) => {
  if (text.length <= MESSAGE_NOTIFY_PREVIEW_MAX_LENGTH) return text;
  return `${text.slice(0, MESSAGE_NOTIFY_PREVIEW_MAX_LENGTH - 1)}…`;
};

/**
 * Fire-and-forget hook for POST /users/message/notify after a plain-text Matrix send.
 * Does not block the caller; failures are reported to Sentry only.
 *
 * @param {{
 *   recipientUserId?: string|null,
 *   senderUserRole?: number|string|null,
 *   receiverUserRole?: number|string|null,
 *   isPriority?: boolean,
 *   enabled?: boolean,
 * }} [options]
 * @returns {(previewText: string, overrides?: Partial<{
 *   recipientUserId: string,
 *   senderUserRole: number,
 *   receiverUserRole: number,
 *   isPriority: boolean,
 * }>) => void}
 */
export const useMessageRecipientNotify = ({
  recipientUserId = null,
  senderUserRole = null,
  receiverUserRole = null,
  isPriority = false,
  enabled = true,
} = {}) => {
  const contextRef = useRef({
    recipientUserId,
    senderUserRole,
    receiverUserRole,
    isPriority,
    enabled,
  });

  contextRef.current = {
    recipientUserId,
    senderUserRole,
    receiverUserRole,
    isPriority,
    enabled,
  };

  return useCallback((previewText, overrides = {}) => {
    const context = contextRef.current;
    if (!context.enabled) return;
    if (isLoginWithSupportPassword()) return;

    if (shouldSkipPrioritySubscriberNotify(previewText)) return;

    const trimmed =
      typeof previewText === 'string' ? previewText.trim() : '';
    if (!trimmed) return;

    const resolvedRecipientUserId =
      (typeof overrides.recipientUserId === 'string'
        ? overrides.recipientUserId.trim()
        : '') ||
      (typeof context.recipientUserId === 'string'
        ? context.recipientUserId.trim()
        : '');

    const resolvedSenderRole = normalizeUserRole(
      overrides.senderUserRole ?? context.senderUserRole
    );
    const resolvedReceiverRole = normalizeUserRole(
      overrides.receiverUserRole ?? context.receiverUserRole
    );
    const resolvedIsPriority = Boolean(
      overrides.isPriority ?? context.isPriority
    );

    if (!resolvedRecipientUserId || resolvedSenderRole == null || resolvedReceiverRole == null) {
      return;
    }

    // Subscriber → creator: only call notify when the subscriber is priority for that creator.
    if (resolvedSenderRole === ROLE.USER && !resolvedIsPriority) {
      return;
    }

    void notifyMessageRecipient({
      recipientUserId: resolvedRecipientUserId,
      previewText: truncatePreviewText(trimmed),
      isPriority: resolvedIsPriority,
      senderUserRole: resolvedSenderRole,
      receiverUserRole: resolvedReceiverRole,
    }).catch((error) => {
      captureChatException(error, {
        tags: { feature: 'chat', action: 'message-recipient-notify' },
        extra: { recipientUserId: resolvedRecipientUserId },
      });
    });
  }, []);
};
